// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { spacing, mediaMaxWithLayoutTheme } from 'presentations/views/styles';
import styled from '@emotion/styled';
import WorkflowListItem from 'presentations/views/components/molecules/WorkflowListItem';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import WorkflowAddButton from '../../molecules/WorkflowAddButton';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import Workflow from 'domains/models/workflow';
import { secureRandomString } from 'domains/models';

interface Props {
  workflows: Workflow[];
  onChange: (workflows: Workflow[]) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [selectedWorkflows, setSelectedWorkflows] = useState<Workflow[]>(
    props.workflows,
  );

  useEffect(() => {
    if (props.workflows) {
      setSelectedWorkflows(props.workflows);
    }
  }, [props.workflows]);

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    ...draggableStyle,
  });

  const getListStyle = (isDraggingOver: boolean) => ({});

  const labelName = (workflowType: WorkflowType) => {
    switch (workflowType) {
      case WorkflowType.QuestionAnswerText:
        return t('AnswerText');
      case WorkflowType.QuestionAnswerEmoji:
        return t('AnswerEmoji');
      case WorkflowType.Task:
        return t('Task');
      case WorkflowType.Kudos:
        return t('Kudos');
      case WorkflowType.Suggestion:
        return t('Suggestions');
      default:
        break;
    }
    return null;
  };

  function onClickAddWorkflow(e: SyntheticEvent<HTMLAnchorElement>) {
    const newWorkflow = new Workflow({
      uid: secureRandomString(),
      workflowType: WorkflowType.QuestionAnswerText,
      content: null,
      isRequired: false,
      hasOtherChoice: false,
    });
    const newWorkflows = [...selectedWorkflows, newWorkflow];
    setSelectedWorkflows(newWorkflows);
    props.onChange(newWorkflows);
  }

  function onClickDelete(workflow: Workflow) {
    for (let i = 0; i < selectedWorkflows.length; i++) {
      const selected = selectedWorkflows[i];
      if (selected.uid === workflow.uid) {
        const newWorkflows = [...selectedWorkflows];
        newWorkflows.splice(i, 1);
        setSelectedWorkflows(newWorkflows);
        props.onChange(newWorkflows);
        break;
      }
    }
  }

  function onDragEnd(result: DropResult) {
    if (!result.destination) {
      return;
    }

    const newWorkflows = [...props.workflows];
    const [moved] = newWorkflows.splice(result.source.index, 1);
    newWorkflows.splice(result.destination.index, 0, moved);
    setSelectedWorkflows(newWorkflows);
    props.onChange(newWorkflows);
  }

  function onChange(e: SyntheticEvent<HTMLInputElement>, workflow: Workflow) {
    const { name, value } = e.currentTarget;

    for (let i = 0; i < selectedWorkflows.length; i++) {
      const selected = selectedWorkflows[i];
      if (selected.uid === workflow.uid) {
        const newWorkflows = [...selectedWorkflows];
        if (name === 'workflowType') {
          newWorkflows[i].workflowType = Number(value);
        }
        if (name === 'content') {
          newWorkflows[i].content = String(value);
        }
        setSelectedWorkflows(newWorkflows);
        props.onChange(selectedWorkflows);
        break;
      }
    }
  }

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {selectedWorkflows.map((workflow, index) => (
                <Draggable
                  key={workflow.uid}
                  draggableId={workflow.uid}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Item
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style,
                      )}
                    >
                      <WorkflowListItem
                        isDraggable={true}
                        isEditable={true}
                        workflow={workflow}
                        label={labelName(workflow.workflowType)}
                        onDelete={onClickDelete}
                        onChange={onChange}
                      />
                    </Item>
                  )}
                </Draggable>
              ))}
              <WorkflowAddButtonContainer>
                <WorkflowAddButton onClick={onClickAddWorkflow}>
                  {t('Add a workflow')}
                </WorkflowAddButton>
              </WorkflowAddButtonContainer>
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
}

const List = styled.div``;
const Item = styled.div`
  margin-bottom: ${spacing(8)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const WorkflowAddButtonContainer = styled.div`
  margin-top: ${spacing(24)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-top: ${spacing(8)};
  }
`;
